import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Barcode from 'react-barcode';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Download, Loader2, Mail } from 'lucide-react';
import SendEmailModal from '../components/SendEmailModal';
import { generateDocPdf } from '../utils/generateDocPdf';
import { useToast } from '../hooks/use-toast';
import RoadRailV2Print from './RoadRailV2Print';

const DocketPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printRef = useRef(null);
  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const pdfFetcher = async () => {
    if (!printRef.current) return null;
    await new Promise(r => setTimeout(r, 300));
    return await generateDocPdf(printRef.current);
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchPrintData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${backendUrl}/api/dockets/${id}/print-data`, {
          headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch docket data');
        const data = await response.json();
        setPrintData(data);
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPrintData();
  }, [id, backendUrl, toast]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center print:hidden">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (!printData) {
    return (
      <div className="min-h-screen flex items-center justify-center print:hidden">
        <p className="text-text-secondary">Docket not found</p>
      </div>
    );
  }

  const { docket, company, resolved_parties, resolved_shipment } = printData;
  const header = docket.payload_json?.header || {};
  const packageInfo = docket.payload_json?.package_info || {};
  const invoices = docket.payload_json?.invoices || [];
  const invoice = invoices[0] || {};
  const isRoadV2 = docket.schema_id === 'ROAD_RAIL_V2';
  const pkgDetails = docket.payload_json?.package_details || {};
  const partyDetails = docket.payload_json?.party_details || {};
  const invDetails = docket.payload_json?.invoice_details || {};
  const vehDetails = docket.payload_json?.vehicle_details || {};
  // AIR/SEA specific payload sections (AirJobSheetForm stores data here)
  const hazBlock = docket.payload_json?.haz_block || {};
  const technicalDetails = docket.payload_json?.technical_details || {};
  const tempLoggerBlock = docket.payload_json?.temperature_logger_block || {};

  // Determine shipment mode checkboxes
  // For INTL_AIR_V1 / INTL_SEA_V1 schemas: default to International when field is not explicitly set
  const isIntlSchema = ['INTL_AIR_V1', 'INTL_SEA_V1'].includes(docket.schema_id);
  const isInternational = header.international_domestic === 'International' || (!header.international_domestic && isIntlSchema);
  const isDomestic = header.international_domestic === 'Domestic' || (!header.international_domestic && !isIntlSchema);
  const isAir  = docket.shipment_type === 'AIR';
  const isSea  = docket.shipment_type === 'SEA';
  const isRoad = docket.shipment_type === 'ROAD';
  const isRail = docket.shipment_type === 'RAIL';
  const isCourier = docket.shipment_type === 'COURIER';

  // Service type — supports both legacy intl types and Road V2 types
  const serviceType = header.service_type || '';
  const isDTD = serviceType === 'Door To Door' || serviceType === 'DTD' || serviceType === 'Refer';
  const isDTP = serviceType === 'Door To Port' || serviceType === 'DTP';
  const isPTP = serviceType === 'Port To Port' || serviceType === 'PTP';
  const isPTD = serviceType === 'Port To Door' || serviceType === 'PTD';
  // commercial_type field (AIR/SEA forms); fallback to serviceType for legacy records
  const commercialType = header.commercial_type || '';
  const isCOMRCL = commercialType === 'Commercial' || (!commercialType && (serviceType === 'Commercial' || serviceType === 'COMRCL' || serviceType === 'General'));
  const isNCOMRCL = commercialType === 'Non Commercial' || (!commercialType && (serviceType === 'Non Commercial' || serviceType === 'N.COMRCL' || serviceType === 'Express'));
  const isRefer = serviceType === 'Refer';

  // Cargo type — form stores: 'General','HAZ','HAZ Temp','Non HAZ Temp','Perishable','Valuable'
  const cargoType = header.cargo_type || '';
  const isGeneral = cargoType === 'General';
  // 'HAZ' and 'HAZ Temp' both tick the HAZ checkbox
  const isHazmat = cargoType === 'HAZ' || cargoType === 'HAZ Temp' || cargoType === 'HAZMAT';
  // 'Non HAZ Temp' and 'HAZ Temp' both tick the NON HAZ TEMP checkbox
  const isTempControlled = cargoType === 'Non HAZ Temp' || cargoType === 'HAZ Temp' || cargoType === 'Temperature Controlled';
  const isPerishable = cargoType === 'Perishable';
  const isValuable = cargoType === 'Valuable';

  return (
    <>
      {/* Print controls - hidden when printing */}
      <div className="print:hidden bg-white border-b sticky top-0 z-10">
        <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />
              Save as PDF
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" onClick={() => setShowEmailModal(true)}
              className="border-orange-500 text-orange-600 hover:bg-orange-50" data-testid="docket-send-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Send Mail
            </Button>
          </div>
        </div>
      </div>

      {/* Non-print wrapper */}
      <div className="min-h-screen bg-gray-100 print:bg-white print:min-h-0">
        <div className="max-w-[900px] mx-auto py-6 px-4 print:py-0 print:px-0 print:max-w-none print:mx-0">
          
          {/* Print Content - This is what gets printed */}
          <div 
            ref={printRef}
            id="print-content"
            className="bg-white shadow-lg print:shadow-none print:w-full"
            style={{ fontFamily: 'Arial, sans-serif' }}
          >
            {isRoadV2 ? (
              /* ── ROAD/RAIL V2 — dedicated logistics print layout ── */
              <div className="p-4 print:p-2">
                <RoadRailV2Print printData={printData} />
              </div>
            ) : (
            <div className="p-6 print:p-8">
              {/* Header with Logo and Company Info */}
              <div className="flex items-start justify-between border-b-2 border-black pb-4 mb-3">
                <div className="flex items-center gap-4">
                  {company?.logo_url ? (
                    <div className="flex items-center justify-center">
                      <img 
                        src={company.logo_url} 
                        alt={company.name} 
                        className="w-40 h-auto object-contain"
                        style={{ maxHeight: '160px' }}
                        onError={(e) => {
                          console.error('Logo failed to load:', company.logo_url);
                          // Try thumbnail URL as fallback
                          const fileId = company.logo_url.match(/id=([^&]+)/)?.[1];
                          if (fileId && !e.target.dataset.retried) {
                            e.target.dataset.retried = 'true';
                            e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-40 h-40 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">
                      LOGO
                    </div>
                  )}
                  <div className="flex-1">
                    <h1 className="text-lg font-bold uppercase">{company?.name || 'COMPANY NAME'}</h1>
                    <p className="text-xs mt-1 leading-tight max-w-md" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                      {company?.address || ''}
                    </p>
                    {(company?.city || company?.state || company?.pincode) && (
                      <p className="text-xs leading-tight">
                        {[company?.city, company?.state, company?.pincode].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {company?.phone && <p className="text-xs mt-0.5">Tel: {company.phone}</p>}
                    {company?.email && <p className="text-xs">Email: {company.email}</p>}
                    {company?.gst_number && <p className="text-xs font-semibold mt-0.5">GSTIN: {company.gst_number}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <h2 className="text-base font-bold">CONSIGNMENT NOTE</h2>
                </div>
              </div>

              {/* Mode Selection Row */}
              <div className="border border-black mb-1">
                <div className="grid grid-cols-3 divide-x divide-black">
                  <div className="p-1.5 flex items-center gap-2">
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isInternational ? 'bg-black' : ''}`}>
                      {isInternational && <span className="text-white text-[10px]">✓</span>}
                    </div>
                    <span className="text-xs font-semibold">INTERNATIONAL</span>
                  </div>
                  <div className="p-1.5 flex items-center gap-2">
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isDomestic ? 'bg-black' : ''}`}>
                      {isDomestic && <span className="text-white text-[10px]">✓</span>}
                    </div>
                    <span className="text-xs font-semibold">DOMESTIC</span>
                  </div>
                  <div className="p-1.5 flex items-center justify-around">
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isAir ? 'bg-black' : ''}`}>
                        {isAir && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">AIR</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isSea ? 'bg-black' : ''}`}>
                        {isSea && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">SEA</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isRoad ? 'bg-black' : ''}`}>
                        {isRoad && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">ROAD</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isRail ? 'bg-black' : ''}`}>
                        {isRail && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">RAIL</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isCourier ? 'bg-black' : ''}`}>
                        {isCourier && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">COURIER</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Type Row */}
              <div className="border border-black mb-1">
                <div className="grid grid-cols-7 divide-x divide-black">
                  <div className="p-1.5 bg-gray-100">
                    <span className="text-xs font-bold">SERVICE</span>
                  </div>
                  <div className="p-1.5 flex items-center justify-center gap-1">
                    <span className="text-xs font-semibold">DTD</span>
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isDTD ? 'bg-black' : ''}`}>
                      {isDTD && <span className="text-white text-[10px]">✓</span>}
                    </div>
                  </div>
                  <div className="p-1.5 flex items-center justify-center gap-1">
                    <span className="text-xs font-semibold">DTP</span>
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isDTP ? 'bg-black' : ''}`}>
                      {isDTP && <span className="text-white text-[10px]">✓</span>}
                    </div>
                  </div>
                  <div className="p-1.5 flex items-center justify-center gap-1">
                    <span className="text-xs font-semibold">PTP</span>
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isPTP ? 'bg-black' : ''}`}>
                      {isPTP && <span className="text-white text-[10px]">✓</span>}
                    </div>
                  </div>
                  <div className="p-1.5 flex items-center justify-center gap-1">
                    <span className="text-xs font-semibold">PTD</span>
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isPTD ? 'bg-black' : ''}`}>
                      {isPTD && <span className="text-white text-[10px]">✓</span>}
                    </div>
                  </div>
                  <div className="p-1.5 flex items-center justify-center gap-1">
                    <span className="text-xs font-semibold">COMRCL</span>
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isCOMRCL ? 'bg-black' : ''}`}>
                      {isCOMRCL && <span className="text-white text-[10px]">✓</span>}
                    </div>
                  </div>
                  <div className="p-1.5 flex items-center justify-center gap-1">
                    <span className="text-xs font-semibold">N.COMRCL</span>
                    <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isNCOMRCL ? 'bg-black' : ''}`}>
                      {isNCOMRCL && <span className="text-white text-[10px]">✓</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* CN Number, Date and Inward/Outward Row */}
              <div className="border border-black mb-1">
                <div className="grid grid-cols-3 divide-x divide-black">
                  <div className="p-1.5">
                    <span className="text-xs font-bold">CN. NO: </span>
                    <span className="text-xs font-bold text-blue-700">{docket.system_docket_no || ''}</span>
                    {docket.system_docket_no && (
                      <div className="mt-1">
                        <Barcode
                          value={docket.system_docket_no}
                          format="CODE128"
                          width={1.2}
                          height={28}
                          fontSize={7}
                          margin={0}
                          displayValue={false}
                        />
                      </div>
                    )}
                  </div>
                  <div className="p-1.5">
                    <span className="text-xs font-bold">DATE & TIME: </span>
                    <span className="text-xs">{formatDateTime(docket.created_at)}</span>
                  </div>
                  <div className="p-1.5 flex items-center justify-around gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-semibold">INWARD</span>
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${header.inward_outward === 'Inward' ? 'bg-black' : ''}`}>
                        {header.inward_outward === 'Inward' && <span className="text-white text-[10px]">✓</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-semibold">OUTWARD</span>
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${header.inward_outward === 'Outward' ? 'bg-black' : ''}`}>
                        {header.inward_outward === 'Outward' && <span className="text-white text-[10px]">✓</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipper and Consignee Section - Now after CN NO */}
              <div className="border border-black mb-1">
                <div className="grid grid-cols-2 divide-x divide-black">
                  {/* FROM (SHIPPER / CONSIGNOR) */}
                  <div className="p-2">
                    <div className="text-xs font-bold border-b border-gray-300 pb-1 mb-1">
                      {isRoadV2 ? 'CONSIGNOR' : 'FROM (SHIPPER)'}
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-semibold">{resolved_parties?.exporter_shipper?.name || ''}</p>
                                            <p className="text-[10px] whitespace-pre-wrap">
                        {isRoadV2 ? (partyDetails.pickup_address || resolved_parties?.exporter_shipper?.address || '') : (resolved_parties?.exporter_shipper?.address || '')}
                      </p>
                      {resolved_parties?.exporter_shipper?.phone && (
                        <p className="text-[10px]">Tel: {resolved_parties.exporter_shipper.phone}</p>
                      )}
                      {resolved_parties?.exporter_shipper?.email && (
                        <p className="text-[10px]">Email: {resolved_parties.exporter_shipper.email}</p>
                      )}
                    </div>
                  </div>
                  {/* TO (CONSIGNEE) */}
                  <div className="p-2">
                    <div className="text-xs font-bold border-b border-gray-300 pb-1 mb-1">
                      {isRoadV2 ? 'CONSIGNEE' : 'TO (CONSIGNEE)'}
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-semibold">{resolved_parties?.importer_receiver?.name || ''}</p>
                                            <p className="text-[10px] whitespace-pre-wrap">
                        {isRoadV2 ? (partyDetails.delivery_address || resolved_parties?.importer_receiver?.address || '') : (resolved_parties?.importer_receiver?.address || '')}
                      </p>
                      {resolved_parties?.importer_receiver?.phone && (
                        <p className="text-[10px]">Tel: {resolved_parties.importer_receiver.phone}</p>
                      )}
                      {resolved_parties?.importer_receiver?.email && (
                        <p className="text-[10px]">Email: {resolved_parties.importer_receiver.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Origin and Destination */}
              <div className="border border-black mb-1">
                <div className="grid grid-cols-2 divide-x divide-black">
                  <div className="p-1.5">
                    <span className="text-xs font-bold">ORIGIN: </span>
                    <span className="text-xs">{resolved_shipment?.loading_from || resolved_shipment?.place_of_receipts || ''}</span>
                  </div>
                  <div className="p-1.5">
                    <span className="text-xs font-bold">DESTINATION: </span>
                    <span className="text-xs">{resolved_shipment?.delivery_at || resolved_shipment?.discharge_at || ''}</span>
                  </div>
                </div>
              </div>

              {/* Package Details Row — ROAD_RAIL_V2 uses new 5-col layout; others use legacy */}
              <div className="border border-black mb-1">
                {isRoadV2 ? (
                  <div className="grid grid-cols-5 divide-x divide-black">
                    <div className="p-1.5 text-center">
                      <div className="text-[10px] font-bold">PACKAGE TYPE</div>
                      <div className="text-xs font-semibold">{pkgDetails.package_type || ''}</div>
                    </div>
                    <div className="p-1.5 text-center">
                      <div className="text-[10px] font-bold">PKGS</div>
                      <div className="text-xs font-semibold">{pkgDetails.no_of_packages || ''}</div>
                    </div>
                    <div className="p-1.5 text-center">
                      <div className="text-[10px] font-bold">DESCRIPTION</div>
                      <div className="text-xs font-semibold">{pkgDetails.description_of_goods || ''}</div>
                    </div>
                    <div className="p-1.5 text-center">
                      <div className="text-[10px] font-bold">GROSS WT.</div>
                      <div className="text-[10px]">KG</div>
                      <div className="text-xs font-semibold">{pkgDetails.actual_weight || ''}</div>
                    </div>
                    <div className="p-1.5 text-center">
                      <div className="text-[10px] font-bold">CH. WEIGHT</div>
                      <div className="text-[10px]">KG</div>
                      <div className="text-xs font-semibold">{pkgDetails.charge_weight || ''}</div>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const showPkgType = ['ROAD_RAIL_V2', 'INTL_AIR_V1', 'INTL_SEA_V1', 'ROAD_RAIL_V1', 'INTL_COURIER_V1'].includes(docket.schema_id) || isCourier;
                    return (
                      <div className={`grid ${showPkgType ? 'grid-cols-5' : 'grid-cols-4'} divide-x divide-black`}>
                        {showPkgType && (
                          <div className="p-1.5 text-center">
                            <div className="text-[10px] font-bold">PACKAGE TYPE</div>
                            <div className="text-xs font-semibold">{header.package_type || ''}</div>
                          </div>
                        )}
                        <div className="p-1.5 text-center">
                          <div className="text-[10px] font-bold">PKGS</div>
                          <div className="text-xs font-semibold">{packageInfo.package_no_of_packages || invoice.no_of_packages || ''}</div>
                        </div>
                        <div className="p-1.5 text-center">
                          <div className="text-[10px] font-bold">GROSS WEIGHT</div>
                          <div className="text-[10px]">KG</div>
                          <div className="text-xs font-semibold">{packageInfo.package_gross_weight || invoice.gross_weight || ''}</div>
                        </div>
                        <div className="p-1.5 text-center">
                          <div className="text-[10px] font-bold">NET WEIGHT</div>
                          <div className="text-[10px]">KG</div>
                          <div className="text-xs font-semibold">{packageInfo.package_net_weight || invoice.net_weight || ''}</div>
                        </div>
                        <div className="p-1.5 text-center">
                          <div className="text-[10px] font-bold">CH. WEIGHT</div>
                          <div className="text-[10px]">KG</div>
                          <div className="text-xs font-semibold">{packageInfo.package_cbm_chargeable_weight || invoice.cbm_chargeable_weight || ''}</div>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>

              {/* Cargo Type Row */}
              <div className="border border-black mb-1">
                <div className="p-1.5">
                  <div className="text-[10px] font-bold mb-1">CARGO TYPE</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isGeneral ? 'bg-black' : ''}`}>
                        {isGeneral && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">GENERAL</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isHazmat ? 'bg-black' : ''}`}>
                        {isHazmat && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">HAZ</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isTempControlled ? 'bg-black' : ''}`}>
                        {isTempControlled && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">NON HAZ TEMP</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isPerishable ? 'bg-black' : ''}`}>
                        {isPerishable && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">PERISHABLE</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${isValuable ? 'bg-black' : ''}`}>
                        {isValuable && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className="text-[10px]">VALUABLE</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="border border-black mb-1">
                <div className="p-1.5">
                  <div className="text-[10px] font-bold">DESCRIPTION</div>
                  <p className="text-xs mt-0.5 min-h-[24px]">
                    {isRoadV2 ? (pkgDetails.description_of_goods || '') : (invoice.description || '')}
                  </p>
                </div>
              </div>

              {/* ROAD_RAIL_V2: Vehicle + Invoice row; others: HAZ Details row */}
              {isRoadV2 ? (
                <>
                  <div className="border border-black mb-1">
                    <div className="grid grid-cols-4 divide-x divide-black">
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">VEHICLE REG NO</div>
                        <div className="text-xs min-h-[16px]">{vehDetails.reg_no || ''}</div>
                      </div>
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">DATA LOGGER NO</div>
                        <div className="text-xs min-h-[16px]">{vehDetails.data_logger_no || ''}</div>
                      </div>
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">TEMPERATURE</div>
                        <div className="text-xs min-h-[16px]">{vehDetails.temperature || ''}</div>
                      </div>
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">INSURANCE</div>
                        <div className="text-xs min-h-[16px]">{invDetails.insurance || ''}</div>
                      </div>
                    </div>
                  </div>
                  <div className="border border-black mb-1">
                    <div className="grid grid-cols-4 divide-x divide-black">
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">INVOICE NO</div>
                        <div className="text-xs min-h-[16px]">{invDetails.invoice_no || ''}</div>
                      </div>
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">INVOICE VALUE</div>
                        <div className="text-xs min-h-[16px]">{invDetails.invoice_value || ''}</div>
                      </div>
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">EWAY BILL NO</div>
                        <div className="text-xs min-h-[16px]">{invDetails.eway_bill_no || ''}</div>
                      </div>
                      <div className="p-1.5">
                        <div className="text-[10px] font-bold">DECLARED VALUE</div>
                        <div className="text-xs min-h-[16px]">{invDetails.declared_value || ''}</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : isCourier ? (
                <div className="border border-black mb-1">
                  <div className="grid grid-cols-4 divide-x divide-black">
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">COURIER PARTNER</div>
                      <div className="text-xs min-h-[16px] font-semibold">{resolved_shipment?.carrier_name || docket.payload_json?.shipment_info?.carrier_name || 'COURIER EXPRESS'}</div>
                    </div>
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">COURIER TRACKING NO</div>
                      <div className="text-xs min-h-[16px] font-mono">{docket.payload_json?.shipment_info?.carrier_docket_no || header.carrier_docket_no || '-'}</div>
                    </div>
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">INVOICE NO / DATE</div>
                      <div className="text-xs min-h-[16px]">{invDetails.invoice_no || invoice.invoice_number || '-'}</div>
                    </div>
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">DECLARED VALUE</div>
                      <div className="text-xs min-h-[16px]">{invDetails.declared_value || invDetails.invoice_value || invoice.invoice_value || '-'}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-black mb-1">
                  <div className="grid grid-cols-4 divide-x divide-black">
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">HAZ CLASS</div>
                      <div className="text-xs min-h-[16px]">{hazBlock.haz_class || header.haz_class || ''}</div>
                    </div>
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">UN NO</div>
                      <div className="text-xs min-h-[16px]">{hazBlock.un_no || header.un_no || ''}</div>
                    </div>
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">DATA LOGGER SR.NO</div>
                      <div className="text-xs min-h-[16px]">{tempLoggerBlock.data_logger_sr_no || header.data_logger_sr_no || ''}</div>
                    </div>
                    <div className="p-1.5">
                      <div className="text-[10px] font-bold">TEMPERATURE</div>
                      <div className="text-xs min-h-[16px]">{technicalDetails.temperature || header.temp || header.temperature || ''}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipper Declaration */}
              <div className="border border-black mb-1">
                <div className="p-1.5">
                  <div className="text-[10px] font-bold mb-0.5">SHIPPER DECLARATION</div>
                  <p className="text-[9px]">
                    I/We hereby declare that the goods described herein are properly classified, described, 
                    packaged, marked and labeled and are in proper condition for transportation according to 
                    the applicable regulations.
                  </p>
                </div>
              </div>

              {/* Signature Row */}
              <div className="border border-black mb-1">
                <div className="grid grid-cols-3 divide-x divide-black">
                  <div className="p-2">
                    <div className="text-[10px] font-bold mb-6">DATE & TIME</div>
                    <div className="border-t border-gray-400 pt-0.5 text-[9px] text-center">Shipper Sign</div>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-bold mb-6">SIGN</div>
                    <div className="border-t border-gray-400 pt-0.5 text-[9px] text-center">Authorized Sign</div>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-bold mb-6">STAMP</div>
                    <div className="border-t border-gray-400 pt-0.5 text-[9px] text-center">Company Stamp</div>
                  </div>
                </div>
              </div>

              {/* Received in Good Condition */}
              <div className="border border-black">
                <div className="p-1.5 bg-gray-100">
                  <div className="text-[10px] font-bold">RECEIVED IN GOOD CONDITION</div>
                </div>
                <div className="grid grid-cols-4 divide-x divide-black">
                  <div className="p-2">
                    <div className="text-[10px] font-bold mb-5">RECEIVER NAME</div>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-bold mb-5">DATE & TIME</div>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-bold mb-5">SIGN</div>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-bold mb-5">STAMP</div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-3 text-center">
                <p className="text-[9px] font-semibold text-gray-600 tracking-wide">ISO 9001:2015 &amp; GDP Certified Company</p>
                <p className="text-[9px] text-gray-500 mt-0.5">This is computer generated document does not require signature</p>
              </div>
            </div>
            )} {/* end isRoadV2 else */}
          </div>
        </div>
      </div>

      {/* Print Styles - Only print the docket content */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 8mm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }

          body * {
            visibility: hidden;
          }

          #print-content, #print-content * {
            visibility: visible;
          }

          #print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .bg-black {
            background-color: #000 !important;
            -webkit-print-color-adjust: exact !important;
          }

          .bg-gray-100 {
            background-color: #f3f4f6 !important;
            -webkit-print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Send Email Modal */}
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        templateKey="docket_created"
        variables={{
          customer_name: printData?.resolved_parties?.importer_receiver?.name || printData?.resolved_parties?.exporter_shipper?.name || '',
          docket_no: printData?.docket?.system_docket_no || '',
          from_location: printData?.resolved_shipment?.loading_from || printData?.resolved_shipment?.place_of_receipts || '',
          to_location: printData?.resolved_shipment?.discharge_at || printData?.resolved_shipment?.delivery_at || '',
          packages: printData?.resolved_shipment?.no_of_packages || '',
          weight: printData?.resolved_shipment?.gross_weight || '',
        }}
        defaultToEmail={printData?.resolved_parties?.importer_receiver?.email || ''}
        defaultSubject={`Shipment Confirmation — ${printData?.docket?.system_docket_no || ''}`}
        pdfFetcher={pdfFetcher}
        pdfFilename={`Docket_${printData?.docket?.system_docket_no || id}.pdf`}
        documentTitle={`Docket — ${printData?.docket?.system_docket_no || ''}`}
        docketId={id}
      />
    </>
  );
};

export default DocketPrint;
